import { useCallback, useMemo } from "react";
import { useParams } from "react-router";
import _ from "lodash";

import {
  CancelIcon,
  DeleteOutlineOutlinedIcon,
  NoteAddOutlinedIcon,
} from "icons";
import theme from "theme";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Box from "components/Box";
import Divider from "components/Divider";
import IconButton from "components/IconButton";
import ListPagination from "components/ListPagination";
import { CircularProgress } from "components/Loading";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import { useConfirmationDialog } from "components/useConfirmationDialog";

import { useMemoPDFGenerationReadyEvent } from "entities/Deal/hooks";
import MemoPDFDocument from "entities/Memo/components/MemoPDFDocument";
import {
  memoPDFDelete,
  MemoPDFStatus,
  useMemoPDFList,
} from "entities/Memo/sdk";

const SavedPDFsTab = () => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const { show: showConfirmationDialog } = useConfirmationDialog();

  const {
    data: memoPDFs,
    mutate: mutateMemoPDFs,
    isLoading: isFetchingMemoPDFs,
    pagesCount,
    setPage,
    currentPage,
  } = useMemoPDFList({
    dealId,
    memoId,
  });

  const onDelete = useCallback(
    ({ memoPDFId }: { memoPDFId: number }) =>
      memoPDFDelete({ dealId, memoId, memoPDFId }),
    [dealId, memoId]
  );

  const handleDelete = useCallback(
    ({ memoPDFId }: { memoPDFId: number }) => {
      showConfirmationDialog({
        onConfirm: () =>
          onDelete({ memoPDFId })
            .then(() => {
              toast.successMessage(
                "PDF document has been successfully deleted!"
              );
              mutateMemoPDFs();
            })
            .catch(handleInvalidRequest),
        confirmButtonText: "Delete",
        message: "Are you sure you want to delete this document?",
      });
    },
    [mutateMemoPDFs, showConfirmationDialog, onDelete]
  );

  const currentPageData = useMemo(
    () => _.get(memoPDFs, "results", []),
    [memoPDFs]
  );

  const onMemoPDFGenerationUpdate = useCallback(
    () => mutateMemoPDFs(),
    [mutateMemoPDFs]
  );

  useMemoPDFGenerationReadyEvent({
    successEventHandler: onMemoPDFGenerationUpdate,
    failureEventHandler: onMemoPDFGenerationUpdate,
  });

  return (
    <>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{
          borderRadius: "4px",
          background: colors.blue10,
          padding: theme.spacing(2, 1.5, 2.5, 2.5),
        }}
      >
        <Stack gap={0.8}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-start"
            gap={1}
          >
            <NoteAddOutlinedIcon width={32} height={32} />
            <Text variant="h3" fontWeight={700}>
              Saved PDFs
            </Text>
          </Stack>
          <Text variant="text3" color={colors.gray80}>
            Saved PDFs can be previewed, downloaded, printed or sent over for
            review (if you prefer a PDF format instead of the digital link
            share). They are are your record-keeping mechanism: save past
            versions of your document or create a final, complete version that
            you may need to get signed.
          </Text>
        </Stack>
      </Stack>

      <Stack padding={theme.spacing(2.5, 0, 0.5, 0)}>
        {!_.isNil(memoPDFs) || !isFetchingMemoPDFs ? (
          <Text variant="text4" color={colors.gray60}>
            {memoPDFs?.count} saved PDF document
            {memoPDFs?.count === 1 ? "" : "s"} created
          </Text>
        ) : (
          <Skeleton width={200} variant="text" height={18} />
        )}
      </Stack>

      <Divider />
      {_.isEmpty(currentPageData) && !isFetchingMemoPDFs && (
        <Text variant="text1" marginTop={1}>
          No PDF documents have been created.
        </Text>
      )}

      {isFetchingMemoPDFs &&
        _.range(15).map(() => (
          <>
            <Stack direction="row" justifyContent="space-between">
              <Stack direction="row" padding={theme.spacing(1.5, 0)} gap={1.5}>
                <Skeleton variant="text" width={400} height={18} />
                <Skeleton variant="text" width={100} height={18} />
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Skeleton variant="text" width={100} height={24} />
                <Skeleton variant="text" width={100} height={24} />
                <Skeleton variant="text" width={20} height={24} />
              </Stack>
            </Stack>
            <Divider />
          </>
        ))}
      {!_.isNil(currentPageData) &&
        !_.isEmpty(currentPageData) &&
        currentPageData.map((memoPDF) => (
          <Stack key={memoPDF.id}>
            <Stack direction="row" justifyContent="space-between">
              {memoPDF.status === MemoPDFStatus.IN_PROGRESS && (
                <Stack direction="row" gap={1} padding={theme.spacing(1.5, 0)}>
                  <CircularProgress size={16} />
                  <Text variant="text4" fontWeight={500} color={colors.blue100}>
                    Document is being generated...
                  </Text>
                </Stack>
              )}
              {memoPDF.status === MemoPDFStatus.FAILED && (
                <>
                  <Stack
                    direction="row"
                    gap={1}
                    padding={theme.spacing(1.5, 0)}
                    width="100%"
                  >
                    <CancelIcon
                      sx={{
                        width: 16,
                        height: 16,
                        color: colors.red120,
                      }}
                    />
                    <Text
                      variant="text4"
                      fontWeight={500}
                      color={colors.red100}
                    >
                      Document generation has failed!
                    </Text>
                  </Stack>
                  <IconButton
                    size="small"
                    onClick={() => handleDelete({ memoPDFId: memoPDF.id })}
                    sx={{
                      alignSelf: "center",
                    }}
                  >
                    <DeleteOutlineOutlinedIcon
                      sx={{
                        width: 20,
                        height: 20,
                      }}
                    />
                  </IconButton>
                </>
              )}
              {memoPDF.status === MemoPDFStatus.SUCCEEDED && (
                <MemoPDFDocument
                  memoPDF={memoPDF}
                  handleDelete={handleDelete}
                />
              )}
            </Stack>
            <Divider />
          </Stack>
        ))}

      <Box marginTop={3}>
        <ListPagination
          setPage={setPage}
          pagesCount={pagesCount}
          currentPage={currentPage}
        />
      </Box>
    </>
  );
};

export default SavedPDFsTab;
